import { useState } from "react";
import { ArrowRight, CheckCircle2, Car, Upload, Loader2, X } from "lucide-react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { PageHero } from "./MarketingLayout";

const fields = [
  { name: "full_name", label: "Full name", required: true, half: true },
  { name: "suburb", label: "Suburb", required: true, half: true },
  { name: "year", label: "Year", required: true, third: true },
  { name: "make", label: "Make", required: true, third: true },
  { name: "model", label: "Model", required: true, third: true },
  { name: "customisation", label: "Customisation brief (mods, paint, wheels, power)", type: "textarea", required: true },
  { name: "instagram", label: "Instagram handle", half: true },
  { name: "mobile", label: "Mobile", half: true },
  { name: "email", label: "Email", type: "email", required: true },
];

export default function ExhibitRide() {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["jpg", "jpeg", "png", "webp"].includes(ext)) {
      return toast.error("Please upload a JPG, PNG or WEBP image.");
    }
    if (file.size > 8 * 1024 * 1024) {
      return toast.error("Image is too large. Max size is 8MB.");
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/exhibit-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_url: `${API}${res.data.url.replace("/api", "")}` }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    for (const f of fields) if (f.required && !form[f.name]) return toast.error(`${f.label} is required`);
    setLoading(true);
    try {
      await api.post("/exhibit-ride", form);
      setDone(true);
      toast.success("Registration received. We'll be in touch.");
    } catch { toast.error("Something went wrong, try again."); }
    finally { setLoading(false); }
  };

  const inp = "w-full bg-[#0f0f11] border border-white/15 px-4 py-3 text-white outline-none focus:border-[#b026ff]";

  return (
    <div>
      <PageHero eyebrow="Early registration" title={<>Exhibit<br />Your Ride</>}
        subtitle="Think your build has what it takes for the floor? Register your ride now for early consideration. Just the basics for now, our team will follow up with the full details." />
      <section className="max-w-2xl mx-auto px-6 pb-24">
        {done ? (
          <div className="border border-[#ff7a1a]/40 bg-[#121212] p-10 text-center" data-testid="exhibit-success">
            <CheckCircle2 className="mx-auto text-[#ffd600] mb-4" size={48} />
            <h3 className="font-heading text-2xl text-white uppercase">You're in the running</h3>
            <p className="text-[#8a8d91] mt-2 font-body">Thanks for registering your ride. Keep an eye on your inbox, we'll reach out with next steps and full submission details soon.</p>
            <a href="/" className="pas-btn mt-6">Back to home</a>
          </div>
        ) : (
          <form onSubmit={submit} className="border border-white/10 bg-[#121212] p-6 sm:p-8" data-testid="exhibit-form">
            <div className="flex items-center gap-2 mb-5 text-[#ff7a1a]"><Car size={22} /><span className="font-heading uppercase">Tell us about your ride</span></div>
            <div className="grid grid-cols-6 gap-3">
              {fields.map((f) => {
                const span = f.third ? "col-span-2" : f.half ? "col-span-3" : "col-span-6";
                return (
                  <div key={f.name} className={span}>
                    {f.type === "textarea" ? (
                      <textarea data-testid={`exhibit-${f.name}`} rows={3} placeholder={f.label + (f.required ? "" : " (optional)")}
                        value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} className={inp} />
                    ) : (
                      <input data-testid={`exhibit-${f.name}`} type={f.type || "text"} placeholder={f.label + (f.required ? "" : " (optional)")}
                        value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} className={inp} />
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-4">
              <div className="font-heading uppercase text-sm text-white mb-1">Photo of your ride</div>
              <p className="text-xs text-[#8a8d91] font-body mb-2">JPG, PNG or WEBP. Max 8MB. Landscape shot at least 1200px wide works best.</p>
              {form.photo_url ? (
                <div className="relative" data-testid="exhibit-photo-preview">
                  <img src={form.photo_url} alt="Your ride" className="w-full aspect-[16/10] object-cover border border-white/10" />
                  <button type="button" onClick={() => setForm((f) => ({ ...f, photo_url: "" }))}
                    className="absolute top-2 right-2 bg-black/70 border border-white/20 text-white p-1.5 hover:text-[#e8321c]" data-testid="exhibit-photo-remove">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <label className="border border-dashed border-white/20 bg-[#0f0f11] p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-[#b026ff]" data-testid="exhibit-photo-upload">
                  {uploading ? <Loader2 className="animate-spin text-[#ff7a1a]" /> : <Upload className="text-[#ff7a1a]" />}
                  <span className="font-heading uppercase text-xs text-[#8a8d91]">{uploading ? "Uploading..." : "Tap to upload a photo"}</span>
                  <input type="file" accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid="exhibit-photo-input" />
                </label>
              )}
            </div>

            <button disabled={loading || uploading} className="pas-btn w-full text-base mt-5" data-testid="exhibit-submit">
              {loading ? "Sending..." : "Register my ride"} <ArrowRight size={18} />
            </button>
            <p className="text-center text-xs text-[#8a8d91] mt-3 font-body">Early registrants get priority consideration for the 2027 show floor.</p>
          </form>
        )}
      </section>
    </div>
  );
}
